import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 4rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  overflow-wrap: anywhere;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: #666;
  flex-grow: 1;
`;

const CardIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: #4A90E2;
`;

const LegalPage: React.FC = () => {
  // Define structured data for legal pages
  const legalStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Legal Information | prest0",
    "description": "Access prest0's legal documents including our privacy policy, terms of service, and cookie policy to understand how we protect your data.",
    "url": "https://prest0.ai/legal",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://prest0.ai/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Legal Information",
          "item": "https://prest0.ai/legal"
        }
      ]
    }
  };

  return (
    <PageContainer>
      <SeoHead
        title="Legal Information | prest0"
        tabTitle="legal"
        description="Access prest0's legal documents: privacy policy, terms of service, and cookie policy governing our enterprise legal AI platform."
        keywords="prest0, legal, privacy policy, terms of service, cookie policy, enterprise legal AI compliance, data protection"
        canonicalUrl="https://prest0.ai/legal"
        structuredData={legalStructuredData}
      />
      
      <HeaderSection>
        <Title>Legal Information</Title>
        <Subtitle>
          Transparency is important to us. Review our legal documents to understand how we protect your data and the terms of using our platform.
        </Subtitle>
      </HeaderSection>
      
      <CategoriesGrid>
        <CategoryCard to="/privacy-policy">
          <CardIcon>🔒</CardIcon>
          <CardTitle>Privacy Policy</CardTitle>
          <CardDescription>
            Learn how we collect, use, and protect your personal information when you use prest0's platform and services.
          </CardDescription>
        </CategoryCard>
        
        <CategoryCard to="/terms-of-service">
          <CardIcon>📝</CardIcon>
          <CardTitle>Terms of Service</CardTitle>
          <CardDescription>
            Understand the terms and conditions that govern your use of prest0's platform, including your rights and responsibilities.
          </CardDescription>
        </CategoryCard>
        
        <CategoryCard to="/cookie-policy">
          <CardIcon>🍪</CardIcon>
          <CardTitle>Cookie Policy</CardTitle>
          <CardDescription>
            Find out how we use cookies and similar technologies to enhance your experience on our website and services.
          </CardDescription>
        </CategoryCard>

        {/* Added 2026-09-11 — the page a security questionnaire is answered
            from, and the only place the full subprocessor list is published. */}
        <CategoryCard to="/trust">
          <CardIcon>🛡️</CardIcon>
          <CardTitle>Trust Center</CardTitle>
          <CardDescription>
            See every company that can process your data, what each one receives, and where it is held — plus our security posture in plain language.
          </CardDescription>
        </CategoryCard>
      </CategoriesGrid>
    </PageContainer>
  );
};

export default LegalPage; 
